import { useCallback, useEffect, useRef, useState } from 'react'
import gql from 'graphql-tag'
import { v4 as uuid } from 'uuid'
import { LlmChatMessageSenderType } from '@/__gen/types'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import { PollLlmChatQuery, usePollLlmChatLazyQuery, useSendMessageMutation } from './__gen/useLlm'

gql`
	mutation sendMessage($id: ID!, $content: String!, $form: ID!, $chat: ID!, $recordId: ID!) {
		llmChatMessage_Update(
			input: { id: $id, content: $content, chat: $chat, record: $recordId, form: $form }
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

const GET_MESSAGES_QUERY = gql`
	query PollLlmChat($chatId: ID!) {
		llmChat(id: $chatId) {
			id
			created
			processingCutoffSeconds
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`

const UNSET_PROCESSING_CUTOFF = -1

export const useLlm = ({ chatId, resetChatId }: { chatId: string; resetChatId: () => void }) => {
	const [sendMessage, { loading: sending }] = useSendMessageMutation()
	const [get, { data }] = usePollLlmChatLazyQuery({})
	const formContext = useFormContext()
	const [startingStream, setStartingStream] = useState(false)
	const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const variables = { chatId }

	const lastMessageTimestamp = data?.llmChat?.messages[data?.llmChat?.messages.length - 1]?.created
	const processingCutoffSeconds = data?.llmChat?.processingCutoffSeconds || UNSET_PROCESSING_CUTOFF

	const resetInactivityTimer = useCallback(() => {
		if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
		if (processingCutoffSeconds === UNSET_PROCESSING_CUTOFF) return
		if (!lastMessageTimestamp) return
		const base = new Date(lastMessageTimestamp).getTime()
		const delay = Math.max(0, base + processingCutoffSeconds * 1000 - Date.now())
		inactivityTimerRef.current = setTimeout(resetChatId, delay)
	}, [processingCutoffSeconds, lastMessageTimestamp])

	useEffect(() => {
		resetInactivityTimer()
		return () => {
			if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
		}
	}, [resetInactivityTimer, processingCutoffSeconds, lastMessageTimestamp])

	const addCacheMessage = ({
		id,
		content,
		sender,
	}: {
		id: string
		content: string
		sender: LlmChatMessageSenderType
	}) => {
		const existing = apolloClient.readQuery<PollLlmChatQuery>({ query: GET_MESSAGES_QUERY, variables })
		apolloClient.writeQuery<PollLlmChatQuery>({
			query: GET_MESSAGES_QUERY,
			variables,
			data: {
				__typename: 'PharosQuery',
				llmChat: {
					__typename: 'LlmChatType',
					id: chatId,
					created: existing?.llmChat?.created ?? new Date().toISOString(),
					processingCutoffSeconds,
					messages: [
						...(existing?.llmChat?.messages ?? []),
						{
							__typename: 'LLmChatMessageType',
							id,
							content,
							created: new Date().toISOString(),
							responseReceivedAt: null,
							sender,
						},
					],
				},
			},
		})
	}

	const send = async ({ content }: { content: string }) => {
		const id = uuid()
		addCacheMessage({ id, content, sender: LlmChatMessageSenderType.User })
		get({ variables, fetchPolicy: 'cache-only' })
		const resp = await sendMessage({
			variables: {
				id,
				chat: chatId,
				content,
				form: formContext.form.id,
				recordId: formContext.record.id,
			},
		})

		if (resp.errors || !resp.data?.llmChatMessage_Update?.object) {
			return
		}

		const messageId = resp.data.llmChatMessage_Update.object.id
		setStartingStream(true)
		await streamResponse(messageId)
	}

	const streamResponse = (messageId: string) =>
		new Promise<void>((resolve) => {
			const id = uuid()
			addCacheMessage({ id, content: '', sender: LlmChatMessageSenderType.Agent })
			const eventSource = new EventSource(`/api/v1/llm/messages/${messageId}/stream/${id}`)
			let accumulated = ''

			eventSource.onmessage = async (event) => {
				setStartingStream(false)
				if (event.data === '[DONE]') {
					eventSource.close()
					await get({ variables, fetchPolicy: 'network-only' })
					resolve()
					return
				}
				try {
					const parsed = JSON.parse(event.data) as { text: string }
					accumulated += parsed.text
					apolloClient.writeFragment({
						id: `LLmChatMessageType:${id}`,
						fragment: gql`
							fragment StreamingMessage on LLmChatMessageType {
								content
							}
						`,
						data: { content: accumulated },
					})
				} catch {
					console.error('Malformed chunk', event.data)
					// ignore malformed chunks
				}
			}

			eventSource.onerror = () => {
				setStartingStream(false)
				eventSource.close()
				resolve()
			}
		})

	return {
		send,
		awaitingResponse: sending || startingStream,
		messages: data?.llmChat?.messages ?? [],
	}
}
